import {
  useState,
  useEffect,
  useMemo,
  useRef,
  createContext,
  useContext,
} from 'react';
import { SystemEntity } from '@erp/shared-types';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { SystemEntityInline } from '@/components/system-entities/inline-ui';
import { InvoiceDetails } from '@/components/accounts-payable/invoice-detail/invoice-details';
import { PurchaseOrderDetails } from '@/components/accounts-payable/purchase-order-detail/purchase-order-details';
import { PaymentDetails } from '@/components/accounts-payable/payment-detail/payment-details';
import { SalesOrderDetails } from '@/components/accounts-receivable/sales-order-detail/sales-order-details';
import { ShipmentDetails } from '@/components/supply-chain/shipment-detail/shipment-details';
import { useQueries } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import { skipToken } from '@tanstack/react-query';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { RELEVANT_ENTITY_TYPES } from '@/api/rpc/hooks/core/useEntityAssociationsFiltered';
/**
 * Loading spinner component for entity details
 */
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-full p-4">
      <CircleNotchIcon className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

/**
 * Error message component for entity details
 */
function ErrorMessage({ message }: { message: string }) {
  return <div className="p-4 text-sm text-muted-foreground">{message}</div>;
}

interface EntityTab {
  entityType: SystemEntity;
  entityId: string;
}

function getEntityKey(entityType: SystemEntity, entityId: string): string {
  return `${entityType}:${entityId}`;
}

interface EntityDetailTabsContextValue {
  entityArray: EntityTab[];
  activeTabId: string;
  setActiveTabId: (id: string) => void;
  renderEntityContent: (entity: EntityTab) => React.ReactNode;
  initialEntityKey: string;
}

const EntityDetailTabsContext =
  createContext<EntityDetailTabsContextValue | null>(null);

export function useEntityDetailTabsContext() {
  const context = useContext(EntityDetailTabsContext);
  if (!context) {
    throw new Error(
      'useEntityDetailTabsContext must be used within EntityDetailTabsProvider',
    );
  }
  return context;
}

interface EntityDetailTabsProviderProps {
  initialEntity: EntityTab;
  children: React.ReactNode;
}

export function EntityDetailTabsProvider({
  initialEntity,
  children,
}: EntityDetailTabsProviderProps) {
  const initialEntityKey = useMemo(
    () => getEntityKey(initialEntity.entityType, initialEntity.entityId),
    [initialEntity.entityType, initialEntity.entityId],
  );

  const [loadedEntities, setLoadedEntities] = useState<Map<string, EntityTab>>(
    () => {
      const map = new Map<string, EntityTab>();
      map.set(initialEntityKey, initialEntity);
      return map;
    },
  );

  const [activeTabId, setActiveTabId] = useState<string>(initialEntityKey);

  // Reset loadedEntities and activeTabId when initialEntity changes
  useEffect(() => {
    const newMap = new Map<string, EntityTab>();
    newMap.set(initialEntityKey, initialEntity);
    setLoadedEntities(newMap);
    setActiveTabId(initialEntityKey);
  }, [initialEntityKey, initialEntity]);

  // Track all entities ever loaded in a ref
  // This ensures hooks are called for all entities we've ever seen
  const allEntitiesEverLoadedRef = useRef<Map<string, EntityTab>>(
    new Map([[initialEntityKey, initialEntity]]),
  );

  // Update ref during render to track all entities ever seen
  loadedEntities.forEach((entity, key) => {
    if (!allEntitiesEverLoadedRef.current.has(key)) {
      allEntitiesEverLoadedRef.current = new Map(
        allEntitiesEverLoadedRef.current,
      );
      allEntitiesEverLoadedRef.current.set(key, entity);
    }
  });

  const allEntitiesEverLoaded = allEntitiesEverLoadedRef.current;

  const entityArray = useMemo(() => {
    const entities = Array.from(loadedEntities.values());
    // Sort to ensure stable hook call order, with initial entity first
    return entities.sort((a, b) => {
      const aKey = getEntityKey(a.entityType, a.entityId);
      const bKey = getEntityKey(b.entityType, b.entityId);

      // Initial entity always comes first
      if (aKey === initialEntityKey) return -1;
      if (bKey === initialEntityKey) return 1;

      // Then sort by entity type and ID
      if (a.entityType !== b.entityType) {
        return a.entityType.localeCompare(b.entityType);
      }
      return a.entityId.localeCompare(b.entityId);
    });
  }, [loadedEntities, initialEntityKey]);

  // Use all entities we've ever loaded to ensure stable hook calls
  const allEntitiesArray = useMemo(() => {
    const entities = Array.from(allEntitiesEverLoaded.values());
    return entities.sort((a, b) => {
      if (a.entityType !== b.entityType) {
        return a.entityType.localeCompare(b.entityType);
      }
      return a.entityId.localeCompare(b.entityId);
    });
  }, [allEntitiesEverLoaded]);

  // Query associations for all entities we've ever seen
  const invoiceEntities = useMemo(
    () =>
      allEntitiesArray
        .filter((e) => e.entityType === SystemEntity.AP_INVOICE)
        .sort((a, b) => a.entityId.localeCompare(b.entityId)),
    [allEntitiesArray],
  );
  const purchaseOrderEntities = useMemo(
    () =>
      allEntitiesArray
        .filter((e) => e.entityType === SystemEntity.INTERNAL_PURCHASE_ORDER)
        .sort((a, b) => a.entityId.localeCompare(b.entityId)),
    [allEntitiesArray],
  );
  const paymentEntities = useMemo(
    () =>
      allEntitiesArray
        .filter((e) => e.entityType === SystemEntity.PAYMENT)
        .sort((a, b) => a.entityId.localeCompare(b.entityId)),
    [allEntitiesArray],
  );
  const salesOrderEntities = useMemo(
    () =>
      allEntitiesArray
        .filter((e) => e.entityType === SystemEntity.AR_SALES_ORDER)
        .sort((a, b) => a.entityId.localeCompare(b.entityId)),
    [allEntitiesArray],
  );
  const shipmentEntities = useMemo(
    () =>
      allEntitiesArray
        .filter((e) => e.entityType === SystemEntity.SCM_SHIPMENT)
        .sort((a, b) => a.entityId.localeCompare(b.entityId)),
    [allEntitiesArray],
  );

  // Query associations using useQueries (handles dynamic arrays properly)
  const invoiceAssociationQueries = useQueries({
    queries: invoiceEntities.map((entity) =>
      orpc.core.entityAssociations.list.queryOptions({
        input: {
          entityId: entity.entityId,
          entityType: SystemEntity.AP_INVOICE,
        },
      }),
    ),
  });

  const purchaseOrderAssociationQueries = useQueries({
    queries: purchaseOrderEntities.map((entity) =>
      orpc.core.entityAssociations.list.queryOptions({
        input: {
          entityId: entity.entityId,
          entityType: SystemEntity.INTERNAL_PURCHASE_ORDER,
        },
      }),
    ),
  });

  const paymentAssociationQueries = useQueries({
    queries: paymentEntities.map((entity) =>
      orpc.core.entityAssociations.list.queryOptions({
        input: {
          entityId: entity.entityId,
          entityType: SystemEntity.PAYMENT,
        },
      }),
    ),
  });

  const salesOrderAssociationQueries = useQueries({
    queries: salesOrderEntities.map((entity) =>
      orpc.core.entityAssociations.list.queryOptions({
        input: {
          entityId: entity.entityId,
          entityType: SystemEntity.AR_SALES_ORDER,
        },
      }),
    ),
  });

  const shipmentAssociationQueries = useQueries({
    queries: shipmentEntities.map((entity) =>
      orpc.core.entityAssociations.list.queryOptions({
        input: {
          entityId: entity.entityId,
          entityType: SystemEntity.SCM_SHIPMENT,
        },
      }),
    ),
  });

  // Filter associations to only relevant entity types
  const filteredInvoiceAssociations = useMemo(() => {
    return invoiceAssociationQueries.map((query) => {
      if (!query.data?.associations) return [];
      return query.data.associations.filter((assoc) =>
        RELEVANT_ENTITY_TYPES.includes(
          assoc.entity.entityType as (typeof RELEVANT_ENTITY_TYPES)[number],
        ),
      );
    });
  }, [invoiceAssociationQueries]);

  const filteredPurchaseOrderAssociations = useMemo(() => {
    return purchaseOrderAssociationQueries.map((query) => {
      if (!query.data?.associations) return [];
      return query.data.associations.filter((assoc) =>
        RELEVANT_ENTITY_TYPES.includes(
          assoc.entity.entityType as (typeof RELEVANT_ENTITY_TYPES)[number],
        ),
      );
    });
  }, [purchaseOrderAssociationQueries]);

  const filteredPaymentAssociations = useMemo(() => {
    return paymentAssociationQueries.map((query) => {
      if (!query.data?.associations) return [];
      return query.data.associations.filter((assoc) =>
        RELEVANT_ENTITY_TYPES.includes(
          assoc.entity.entityType as (typeof RELEVANT_ENTITY_TYPES)[number],
        ),
      );
    });
  }, [paymentAssociationQueries]);

  const filteredSalesOrderAssociations = useMemo(() => {
    return salesOrderAssociationQueries.map((query) => {
      if (!query.data?.associations) return [];
      return query.data.associations.filter((assoc) =>
        RELEVANT_ENTITY_TYPES.includes(
          assoc.entity.entityType as (typeof RELEVANT_ENTITY_TYPES)[number],
        ),
      );
    });
  }, [salesOrderAssociationQueries]);

  const filteredShipmentAssociations = useMemo(() => {
    return shipmentAssociationQueries.map((query) => {
      if (!query.data?.associations) return [];
      return query.data.associations.filter((assoc) =>
        RELEVANT_ENTITY_TYPES.includes(
          assoc.entity.entityType as (typeof RELEVANT_ENTITY_TYPES)[number],
        ),
      );
    });
  }, [shipmentAssociationQueries]);

  // Query entity data using useQueries (handles dynamic arrays properly)
  const invoiceDataQueries = useQueries({
    queries: invoiceEntities.map((entity) =>
      orpc.accountsPayable.invoices.get.queryOptions({
        input: entity.entityId ? { id: entity.entityId } : skipToken,
      }),
    ),
  });

  const purchaseOrderDataQueries = useQueries({
    queries: purchaseOrderEntities.map((entity) =>
      orpc.accountsPayable.purchaseOrders.get.queryOptions({
        input: entity.entityId ? { id: entity.entityId } : skipToken,
      }),
    ),
  });

  const paymentDataQueries = useQueries({
    queries: paymentEntities.map((entity) =>
      orpc.finance.payments.get.queryOptions({
        input: entity.entityId ? { id: entity.entityId } : skipToken,
      }),
    ),
  });

  const salesOrderDataQueries = useQueries({
    queries: salesOrderEntities.map((entity) =>
      orpc.accountsReceivable.salesOrders.get.queryOptions({
        input: entity.entityId ? { id: entity.entityId } : skipToken,
      }),
    ),
  });

  const shipmentDataQueries = useQueries({
    queries: shipmentEntities.map((entity) =>
      orpc.supplyChain.shipments.get.queryOptions({
        input: entity.entityId ? { id: entity.entityId } : skipToken,
      }),
    ),
  });

  // Create maps for easy lookup
  const invoiceDataMap = useMemo(() => {
    const map = new Map<string, (typeof invoiceDataQueries)[0]>();
    invoiceEntities.forEach((entity, index) => {
      const key = getEntityKey(entity.entityType, entity.entityId);
      map.set(key, invoiceDataQueries[index]);
    });
    return map;
  }, [invoiceEntities, invoiceDataQueries]);

  const purchaseOrderDataMap = useMemo(() => {
    const map = new Map<string, (typeof purchaseOrderDataQueries)[0]>();
    purchaseOrderEntities.forEach((entity, index) => {
      const key = getEntityKey(entity.entityType, entity.entityId);
      map.set(key, purchaseOrderDataQueries[index]);
    });
    return map;
  }, [purchaseOrderEntities, purchaseOrderDataQueries]);

  const paymentDataMap = useMemo(() => {
    const map = new Map<string, (typeof paymentDataQueries)[0]>();
    paymentEntities.forEach((entity, index) => {
      const key = getEntityKey(entity.entityType, entity.entityId);
      map.set(key, paymentDataQueries[index]);
    });
    return map;
  }, [paymentEntities, paymentDataQueries]);

  const salesOrderDataMap = useMemo(() => {
    const map = new Map<string, (typeof salesOrderDataQueries)[0]>();
    salesOrderEntities.forEach((entity, index) => {
      const key = getEntityKey(entity.entityType, entity.entityId);
      map.set(key, salesOrderDataQueries[index]);
    });
    return map;
  }, [salesOrderEntities, salesOrderDataQueries]);

  const shipmentDataMap = useMemo(() => {
    const map = new Map<string, (typeof shipmentDataQueries)[0]>();
    shipmentEntities.forEach((entity, index) => {
      const key = getEntityKey(entity.entityType, entity.entityId);
      map.set(key, shipmentDataQueries[index]);
    });
    return map;
  }, [shipmentEntities, shipmentDataQueries]);

  // Add new entities from associations
  useEffect(() => {
    const newEntities = new Map(loadedEntities);

    // Process filtered associations
    filteredInvoiceAssociations.forEach((associations) => {
      associations.forEach((assoc) => {
        const key = getEntityKey(
          assoc.entity.entityType,
          assoc.entity.entityId,
        );
        if (!newEntities.has(key)) {
          newEntities.set(key, {
            entityType: assoc.entity.entityType,
            entityId: assoc.entity.entityId,
          });
        }
      });
    });

    filteredPurchaseOrderAssociations.forEach((associations) => {
      associations.forEach((assoc) => {
        const key = getEntityKey(
          assoc.entity.entityType,
          assoc.entity.entityId,
        );
        if (!newEntities.has(key)) {
          newEntities.set(key, {
            entityType: assoc.entity.entityType,
            entityId: assoc.entity.entityId,
          });
        }
      });
    });

    filteredPaymentAssociations.forEach((associations) => {
      associations.forEach((assoc) => {
        const key = getEntityKey(
          assoc.entity.entityType,
          assoc.entity.entityId,
        );
        if (!newEntities.has(key)) {
          newEntities.set(key, {
            entityType: assoc.entity.entityType,
            entityId: assoc.entity.entityId,
          });
        }
      });
    });

    filteredSalesOrderAssociations.forEach((associations) => {
      associations.forEach((assoc) => {
        const key = getEntityKey(
          assoc.entity.entityType,
          assoc.entity.entityId,
        );
        if (!newEntities.has(key)) {
          newEntities.set(key, {
            entityType: assoc.entity.entityType,
            entityId: assoc.entity.entityId,
          });
        }
      });
    });

    filteredShipmentAssociations.forEach((associations) => {
      associations.forEach((assoc) => {
        const key = getEntityKey(
          assoc.entity.entityType,
          assoc.entity.entityId,
        );
        if (!newEntities.has(key)) {
          newEntities.set(key, {
            entityType: assoc.entity.entityType,
            entityId: assoc.entity.entityId,
          });
        }
      });
    });

    // Also check for direct payment.invoice relationships
    paymentDataQueries.forEach((query) => {
      if (!query.data?.invoice) return;

      const invoice = query.data.invoice;
      const key = getEntityKey(SystemEntity.AP_INVOICE, invoice.id);
      if (!newEntities.has(key)) {
        newEntities.set(key, {
          entityType: SystemEntity.AP_INVOICE,
          entityId: invoice.id,
        });
      }
    });

    if (newEntities.size !== loadedEntities.size) {
      setLoadedEntities(newEntities);
    }
  }, [
    filteredInvoiceAssociations,
    filteredPurchaseOrderAssociations,
    filteredPaymentAssociations,
    filteredSalesOrderAssociations,
    filteredShipmentAssociations,
    paymentDataQueries,
    loadedEntities,
  ]);

  // Render entity detail content based on type
  function renderEntityContent(entity: EntityTab) {
    const key = getEntityKey(entity.entityType, entity.entityId);

    if (entity.entityType === SystemEntity.AP_INVOICE) {
      const query = invoiceDataMap.get(key);
      if (!query) {
        return <ErrorMessage message="Loading..." />;
      }
      const { data: invoiceData, isLoading, isError } = query;
      if (isLoading) {
        return <LoadingSpinner />;
      }
      if (isError || !invoiceData?.invoice) {
        return <ErrorMessage message="Unable to load invoice details" />;
      }
      return (
        <InvoiceDetails
          invoice={invoiceData.invoice}
          inboundEmail={invoiceData.inboundEmail}
          lineItems={invoiceData.lineItems}
        />
      );
    } else if (entity.entityType === SystemEntity.INTERNAL_PURCHASE_ORDER) {
      const query = purchaseOrderDataMap.get(key);
      if (!query) {
        return <ErrorMessage message="Loading..." />;
      }
      const { data: purchaseOrderData, isLoading, isError } = query;
      if (isLoading) {
        return <LoadingSpinner />;
      }
      if (isError || !purchaseOrderData?.purchaseOrder) {
        return <ErrorMessage message="Unable to load purchase order details" />;
      }
      return (
        <PurchaseOrderDetails
          purchaseOrder={purchaseOrderData.purchaseOrder}
          lineItems={purchaseOrderData.lineItems || []}
        />
      );
    } else if (entity.entityType === SystemEntity.PAYMENT) {
      const query = paymentDataMap.get(key);
      if (!query) {
        return <ErrorMessage message="Loading..." />;
      }
      const { data: payment, isLoading, isError } = query;
      if (isLoading) {
        return <LoadingSpinner />;
      }
      if (isError || !payment) {
        return <ErrorMessage message="Unable to load payment details" />;
      }
      return <PaymentDetails payment={payment} />;
    } else if (entity.entityType === SystemEntity.AR_SALES_ORDER) {
      const query = salesOrderDataMap.get(key);
      if (!query) {
        return <ErrorMessage message="Loading..." />;
      }
      const { data: salesOrderData, isLoading, isError } = query;
      if (isLoading) {
        return <LoadingSpinner />;
      }
      if (isError || !salesOrderData?.salesOrder) {
        return <ErrorMessage message="Unable to load sales order details" />;
      }
      return (
        <SalesOrderDetails
          salesOrder={salesOrderData.salesOrder}
          lineItems={salesOrderData.lineItems || []}
        />
      );
    } else if (entity.entityType === SystemEntity.SCM_SHIPMENT) {
      const query = shipmentDataMap.get(key);
      if (!query) {
        return <ErrorMessage message="Loading..." />;
      }
      const { data: shipmentData, isLoading, isError } = query;
      if (isLoading) {
        return <LoadingSpinner />;
      }
      if (isError || !shipmentData?.shipment) {
        return <ErrorMessage message="Unable to load shipment details" />;
      }
      return (
        <ShipmentDetails
          shipment={shipmentData.shipment}
          salesOrders={shipmentData.salesOrders || []}
          purchaseOrders={shipmentData.purchaseOrders || []}
          chargesCount={shipmentData.chargesCount || 0}
        />
      );
    }

    return (
      <div className="p-4 text-sm text-muted-foreground">
        Unknown entity type
      </div>
    );
  }

  const contextValue: EntityDetailTabsContextValue = {
    entityArray,
    activeTabId,
    setActiveTabId,
    renderEntityContent,
    initialEntityKey,
  };

  return (
    <EntityDetailTabsContext.Provider value={contextValue}>
      {children}
    </EntityDetailTabsContext.Provider>
  );
}

export function EntityDetailTabsHeader() {
  const { entityArray, initialEntityKey } = useEntityDetailTabsContext();

  if (entityArray.length === 0) {
    return null;
  }

  return (
    <TabsList>
      {entityArray.map((entity) => {
        const key = getEntityKey(entity.entityType, entity.entityId);
        const isInitialEntity = key === initialEntityKey;
        return (
          <TabsTrigger
            key={key}
            value={key}
            className={cn(
              isInitialEntity &&
                'font-semibold data-[state=active]:text-primary',
            )}
          >
            <SystemEntityInline
              entityType={entity.entityType}
              entityId={entity.entityId}
            />
          </TabsTrigger>
        );
      })}
    </TabsList>
  );
}

export function EntityDetailTabsContent() {
  const { entityArray, renderEntityContent } = useEntityDetailTabsContext();

  if (entityArray.length === 0) {
    return (
      <div className="p-4 text-sm text-muted-foreground">No entities found</div>
    );
  }

  return (
    <div className="h-full">
      {entityArray.map((entity) => {
        const key = getEntityKey(entity.entityType, entity.entityId);
        return (
          <TabsContent
            key={key}
            value={key}
            className="mt-0 h-full data-[state=active]:flex data-[state=active]:flex-col"
          >
            {renderEntityContent(entity)}
          </TabsContent>
        );
      })}
    </div>
  );
}

interface EntityDetailTabsProps {
  initialEntity: EntityTab;
}

export function EntityDetailTabs({ initialEntity }: EntityDetailTabsProps) {
  return (
    <EntityDetailTabsProvider initialEntity={initialEntity}>
      <EntityDetailTabsInner />
    </EntityDetailTabsProvider>
  );
}

function EntityDetailTabsInner() {
  const { activeTabId, setActiveTabId } = useEntityDetailTabsContext();
  return (
    <Tabs
      value={activeTabId}
      onValueChange={setActiveTabId}
      className="flex flex-col h-full"
    >
      <EntityDetailTabsHeader />
      <EntityDetailTabsContent />
    </Tabs>
  );
}
